import { useState, useCallback, useEffect } from 'react'
import { Search, Terminal, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import {
  PayloadEditor, CopyButton, CurlCollapsible, SectionLabel, syntaxHighlight, inputCls, labelCls,
} from './components/shared'
import {
  ENDPOINTS, ENDPOINT_CATEGORIES, type EndpointDef, type HttpMethod, type EndpointCategory,
} from './explorer-endpoints'
import { proxyUrl, buildHeaders } from './utils'
import { DEFAULT_CREDS } from './data'
import type { Creds, LogEntry } from './types'

// ── Types ──────────────────────────────────────────────────────────────────────

interface HistoryEntry {
  id: string
  timestamp: string
  endpointId: string
  method: HttpMethod
  resolvedPath: string
  pathParams: Record<string, string>
  requestBody?: string
  responseStatus: number
  durationMs: number
}

interface ResponseState {
  status: number
  durationMs: number
  data: unknown
  method: string
  resolvedPath: string
}

// ── Constants ──────────────────────────────────────────────────────────────────

const HISTORY_KEY = 'ft-explorer-history'
const MAX_HISTORY = 50
const LARGE_RESPONSE_THRESHOLD = 500

const EXTRACTABLE_FIELDS = ['entityId', 'workflowExecutionId', 'documentId', 'attachmentId'] as const
type ExtractableField = typeof EXTRACTABLE_FIELDS[number]

// ── Helpers ────────────────────────────────────────────────────────────────────

function resolvePath(template: string, params: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => params[key] || `{${key}}`)
}

function extractAutoFillValues(body: unknown): Record<string, string> {
  const values: Record<string, string> = {}
  function scan(obj: unknown) {
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
        if (EXTRACTABLE_FIELDS.includes(key as ExtractableField) && typeof val === 'string') {
          values[key] = val
        }
        if (typeof val === 'object' && val !== null) scan(val)
      }
    }
    if (Array.isArray(obj)) obj.forEach(scan)
  }
  scan(body)
  return values
}

function statusText(code: number): string {
  if (code === 200) return 'OK'
  if (code === 201) return 'Created'
  if (code === 202) return 'Accepted'
  if (code === 204) return 'No Content'
  if (code === 400) return 'Bad Request'
  if (code === 401) return 'Unauthorized'
  if (code === 403) return 'Forbidden'
  if (code === 404) return 'Not Found'
  if (code === 409) return 'Conflict'
  if (code === 422) return 'Unprocessable Entity'
  if (code === 500) return 'Internal Server Error'
  return ''
}

function loadHistory(): HistoryEntry[] {
  try {
    const stored = localStorage.getItem(HISTORY_KEY)
    if (stored) return JSON.parse(stored) as HistoryEntry[]
  } catch { /* ignore */ }
  return []
}

function saveHistory(entries: HistoryEntry[]): void {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, MAX_HISTORY))) } catch { /* ignore */ }
}

function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

// ── Method Badge ──────────────────────────────────────────────────────────────

function MethodBadge({ method, large = false }: { method: HttpMethod; large?: boolean }) {
  const styles: Record<HttpMethod, { bg: string; color: string }> = {
    GET: { bg: 'rgba(56,189,248,.15)', color: '#38bdf8' },
    POST: { bg: 'rgba(52,211,153,.15)', color: '#34d399' },
    PATCH: { bg: 'rgba(251,191,36,.15)', color: '#fbbf24' },
    DELETE: { bg: 'rgba(248,113,113,.15)', color: '#f87171' },
  }
  const s = styles[method]
  return (
    <span
      className={`font-mono font-bold rounded shrink-0 ${large ? 'px-2 py-1 text-[11px]' : 'px-1.5 py-0.5 text-[9px]'}`}
      style={{ background: s.bg, color: s.color }}
    >
      {method}
    </span>
  )
}

// ── Status Badge ──────────────────────────────────────────────────────────────

function StatusBadge({ code }: { code: number }) {
  const is2xx = code >= 200 && code < 300
  const style = is2xx
    ? { bg: 'rgba(52,211,153,.15)', color: '#34d399' }
    : { bg: 'rgba(248,113,113,.15)', color: '#f87171' }
  return (
    <span className="px-2 py-0.5 rounded text-[11px] font-bold font-mono shrink-0" style={{ background: style.bg, color: style.color }}>
      {code} {statusText(code)}
    </span>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

interface ExplorerWorkspaceProps {
  creds: Creds
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
}

export function ExplorerWorkspace({ creds, addLog }: ExplorerWorkspaceProps) {
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)

  // Endpoint picker state
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState<EndpointCategory | null>(null)
  const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointDef | null>(null)

  // Request builder state
  const [pathParams, setPathParams] = useState<Record<string, string>>({})
  const [bodyText, setBodyText] = useState('')
  const [bodyDirty, setBodyDirty] = useState(false)
  const [bodyError, setBodyError] = useState<string | null>(null)

  // Auto-fill state (extracted from previous responses)
  const [autoFillValues, setAutoFillValues] = useState<Record<string, string>>({})

  // Response state
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<ResponseState | null>(null)
  const [extractedValues, setExtractedValues] = useState<[string, string][]>([])
  const [showAllLines, setShowAllLines] = useState(false)

  // History state
  const [history, setHistory] = useState<HistoryEntry[]>(() => loadHistory())
  const [historyOpen, setHistoryOpen] = useState(false)

  // When endpoint changes, initialise path params and body
  useEffect(() => {
    if (!selectedEndpoint) return
    const defaults: Record<string, string> = {}
    for (const p of selectedEndpoint.pathParams) {
      defaults[p.name] = autoFillValues[p.name] ?? p.defaultValue ?? ''
    }
    setPathParams(defaults)
    setBodyText(selectedEndpoint.hasRequestBody
      ? JSON.stringify(selectedEndpoint.examplePayload ?? {}, null, 2)
      : '')
    setBodyDirty(false)
    setBodyError(null)
    setResponse(null)
    setExtractedValues([])
    setShowAllLines(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEndpoint?.id])

  // Filtered endpoint list
  const filtered = ENDPOINTS.filter(ep => {
    const q = search.toLowerCase()
    const matchesSearch = !q
      || ep.summary.toLowerCase().includes(q)
      || ep.pathTemplate.toLowerCase().includes(q)
      || ep.method.toLowerCase().includes(q)
    const matchesCategory = !activeCategory || ep.category === activeCategory
    return matchesSearch && matchesCategory
  })

  // Category counts (against current search, ignore category filter for counts)
  const categoryCounts = Object.fromEntries(
    ENDPOINT_CATEGORIES.map(cat => [
      cat,
      ENDPOINTS.filter(ep => {
        const q = search.toLowerCase()
        return (!q || ep.summary.toLowerCase().includes(q) || ep.pathTemplate.toLowerCase().includes(q) || ep.method.toLowerCase().includes(q))
          && ep.category === cat
      }).length,
    ])
  )

  // Grouped endpoints for display
  const grouped = ENDPOINT_CATEGORIES.reduce<Record<EndpointCategory, EndpointDef[]>>((acc, cat) => {
    acc[cat] = filtered.filter(ep => ep.category === cat)
    return acc
  }, {} as Record<EndpointCategory, EndpointDef[]>)

  const resolvedPath = selectedEndpoint
    ? resolvePath(selectedEndpoint.pathTemplate, pathParams)
    : ''

  const missingRequired = selectedEndpoint
    ? selectedEndpoint.pathParams.filter(p => p.required && !pathParams[p.name]?.trim())
    : []

  const doFetch = useCallback(async (method: string, path: string, body: unknown | undefined, annotation: string) => {
    const url = `${apiBase}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(creds, method !== 'GET' && method !== 'DELETE'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    return { data, status: res.status, durationMs }
  }, [apiBase, creds, addLog])

  async function handleSend() {
    if (!selectedEndpoint || loading || missingRequired.length > 0) return

    let body: unknown | undefined
    if (selectedEndpoint.hasRequestBody) {
      try {
        body = JSON.parse(bodyText) as unknown
      } catch {
        setBodyError('Invalid JSON — fix syntax before sending')
        return
      }
    }

    setLoading(true)
    setResponse(null)
    setExtractedValues([])
    setBodyError(null)

    try {
      const { data, status, durationMs } = await doFetch(
        selectedEndpoint.method,
        resolvedPath,
        body,
        `${selectedEndpoint.summary} — ${selectedEndpoint.pathTemplate}`,
      )

      setResponse({ status, durationMs, data, method: selectedEndpoint.method, resolvedPath })

      // Extract auto-fill values
      const extracted = extractAutoFillValues(data)
      setExtractedValues(Object.entries(extracted))

      // Persist to history
      const entry: HistoryEntry = {
        id: genId(),
        timestamp: new Date().toISOString(),
        endpointId: selectedEndpoint.id,
        method: selectedEndpoint.method,
        resolvedPath,
        pathParams: { ...pathParams },
        requestBody: selectedEndpoint.hasRequestBody ? bodyText : undefined,
        responseStatus: status,
        durationMs,
      }
      const next = [entry, ...history].slice(0, MAX_HISTORY)
      setHistory(next)
      saveHistory(next)
    } catch { /* logged by doFetch */ } finally {
      setLoading(false)
    }
  }

  function handleBodyChange(v: string) {
    setBodyText(v)
    setBodyDirty(true)
    setBodyError(null)
  }

  function handleBodyReset() {
    setBodyText(JSON.stringify(selectedEndpoint?.examplePayload ?? {}, null, 2))
    setBodyDirty(false)
    setBodyError(null)
  }

  function handleAutoFillSave(key: string, val: string) {
    setAutoFillValues(prev => ({ ...prev, [key]: val }))
  }

  function replayHistory(entry: HistoryEntry) {
    const ep = ENDPOINTS.find(e => e.id === entry.endpointId)
    if (!ep) return
    setSelectedEndpoint(ep)
    // Use a timeout so the useEffect for endpoint change fires first, then we overwrite
    setTimeout(() => {
      setPathParams(entry.pathParams)
      if (entry.requestBody !== undefined) {
        setBodyText(entry.requestBody)
        setBodyDirty(true)
      }
    }, 0)
  }

  function clearHistory() {
    setHistory([])
    saveHistory([])
  }

  // Response JSON lines for "show all" toggle
  const responseJson = response ? JSON.stringify(response.data, null, 2) : ''
  const responseLines = responseJson.split('\n')
  const isTruncated = responseLines.length > LARGE_RESPONSE_THRESHOLD
  const displayedLines = isTruncated && !showAllLines
    ? responseLines.slice(0, 100).join('\n')
    : responseJson

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: 'minmax(390px, 430px) 1fr', alignItems: 'start', padding: '18px 0' }}
    >
      {/* ── LEFT PANE ── */}
      <div
        className="rounded-[14px] overflow-hidden"
        style={{ background: '#111c30', border: '1px solid #1e2c44' }}
      >
        {/* Header strip */}
        <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
          <span className="text-[11px] font-semibold text-[#94a3b8]">API Explorer</span>
          <span className="text-[9px] text-[#3f4f6b]">{ENDPOINTS.length} endpoints</span>
        </div>

        <div className="p-4 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#3f4f6b]" />
            <input
              className={`${inputCls} pl-7`}
              placeholder="Search endpoints…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Category filter chips */}
          <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {ENDPOINT_CATEGORIES.filter(cat => categoryCounts[cat] > 0).map(cat => {
              const isActive = activeCategory === cat
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(isActive ? null : cat)}
                  className="flex items-center gap-1 px-2 py-1 rounded-[6px] text-[10px] font-medium whitespace-nowrap shrink-0 transition-all"
                  style={isActive
                    ? { background: 'rgba(99,102,241,.16)', border: '1px solid rgba(99,102,241,.45)', color: '#e2e8f0' }
                    : { background: '#0f1a2e', border: '1px solid #233149', color: '#5b6b85' }
                  }
                >
                  {cat}
                  <span
                    className="text-[9px] px-1 py-0.5 rounded"
                    style={{ background: isActive ? 'rgba(99,102,241,.2)' : '#0a1322', color: isActive ? '#c7d2fe' : '#3f4f6b' }}
                  >
                    {categoryCounts[cat]}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Endpoint list */}
          <div className="overflow-y-auto space-y-0" style={{ maxHeight: 350 }}>
            {ENDPOINT_CATEGORIES.map(cat => {
              const eps = grouped[cat]
              if (!eps.length) return null
              return (
                <div key={cat}>
                  <div
                    className="sticky top-0 px-2 py-1 text-[9px] font-bold uppercase tracking-wider"
                    style={{ background: '#0a1322', color: '#3f4f6b', zIndex: 1 }}
                  >
                    {cat}
                  </div>
                  {eps.map(ep => {
                    const isSelected = selectedEndpoint?.id === ep.id
                    return (
                      <button
                        key={ep.id}
                        onClick={() => setSelectedEndpoint(ep)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-[6px] text-left transition-colors"
                        style={isSelected
                          ? { background: 'rgba(99,102,241,.08)' }
                          : { background: 'transparent' }
                        }
                      >
                        <MethodBadge method={ep.method} />
                        <span className="text-[10px] font-mono text-[#7e8ba6] truncate flex-1" title={ep.pathTemplate}>
                          {ep.pathTemplate}
                        </span>
                        <span className="text-[10px] text-[#5b6b85] truncate max-w-[90px] text-right shrink-0">
                          {ep.summary}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )
            })}
            {filtered.length === 0 && (
              <p className="text-[11px] text-[#3f4f6b] italic px-2 py-4 text-center">No endpoints match</p>
            )}
          </div>
        </div>

        {/* Request builder — shown when an endpoint is selected */}
        {selectedEndpoint && (
          <div style={{ borderTop: '1px solid #1e2c44' }}>
            <div className="p-4 space-y-3">
              {/* Endpoint header */}
              <div className="flex items-center gap-2 flex-wrap">
                <MethodBadge method={selectedEndpoint.method} large />
                <span className="text-[11px] font-mono text-[#8aa0c2] break-all flex-1">
                  {resolvedPath}
                </span>
              </div>

              {/* Path Parameters */}
              {selectedEndpoint.pathParams.length > 0 && (
                <div>
                  <SectionLabel>Path Parameters</SectionLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedEndpoint.pathParams.map(param => {
                      const hasAutoFill = !!autoFillValues[param.name]
                      return (
                        <div key={param.name}>
                          <label className={labelCls}>
                            <span className="flex items-center gap-1">
                              {param.name}
                              {param.required && <span className="text-red-400">*</span>}
                              {hasAutoFill && (
                                <span
                                  className="w-[5px] h-[5px] rounded-full inline-block"
                                  style={{ background: '#34d399' }}
                                  title="Auto-filled from previous response"
                                />
                              )}
                            </span>
                          </label>
                          <input
                            className={inputCls}
                            placeholder={param.description}
                            value={pathParams[param.name] ?? ''}
                            onChange={e => setPathParams(p => ({ ...p, [param.name]: e.target.value }))}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Request Body */}
              {selectedEndpoint.hasRequestBody && (
                <PayloadEditor
                  value={bodyText}
                  dirty={bodyDirty}
                  error={bodyError}
                  onChange={handleBodyChange}
                  onReset={handleBodyReset}
                />
              )}

              {/* Send button */}
              <button
                onClick={() => void handleSend()}
                disabled={loading || missingRequired.length > 0}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading
                  ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Sending…</span>
                  : 'Send Request'
                }
              </button>

              {!hasCreds && (
                <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>
              )}

              {/* cURL preview */}
              <CurlCollapsible
                method={selectedEndpoint.method}
                url={`${apiBase}${resolvedPath}`}
                creds={creds}
                body={selectedEndpoint.hasRequestBody ? (() => { try { return JSON.parse(bodyText) as unknown } catch { return undefined } })() : undefined}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── RIGHT PANE ── */}
      <div className="space-y-3 min-w-0">
        {/* Idle */}
        {!loading && !response && (
          <div
            className="flex flex-col items-center justify-center py-16 rounded-[14px]"
            style={{ border: '2px dashed #233149', background: '#0d1626' }}
          >
            <div
              className="w-12 h-12 rounded-[12px] flex items-center justify-center mb-4"
              style={{ background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)' }}
            >
              <Terminal size={22} className="text-[#6366f1]" />
            </div>
            <p className="text-[14px] font-semibold text-[#5b6b85]">Select an endpoint and send a request</p>
            <p className="text-[11px] text-[#3f4f6b] mt-1.5 text-center max-w-xs">
              Browse the catalogue or search by name
            </p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="p-5 rounded-[14px]" style={{ background: '#0e1828', border: '1px solid #2a3754' }}>
            <div className="flex items-center gap-3">
              <Loader2 size={18} className="animate-spin text-[#6366f1] shrink-0" />
              <p className="text-[13px] font-semibold text-[#94a3b8]">Sending request…</p>
            </div>
          </div>
        )}

        {/* Response */}
        {!loading && response && (
          <div className="space-y-3" style={{ animation: 'fadeUp .25s ease' }}>
            {/* Status header */}
            {(() => {
              const is2xx = response.status >= 200 && response.status < 300
              return (
                <div
                  className="p-4 rounded-[14px]"
                  style={{
                    background: is2xx ? 'rgba(52,211,153,.06)' : 'rgba(248,113,113,.06)',
                    border: `1px solid ${is2xx ? 'rgba(52,211,153,.2)' : 'rgba(248,113,113,.2)'}`,
                  }}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <StatusBadge code={response.status} />
                    <span className="text-[11px] text-[#5b6b85]">{response.durationMs}ms</span>
                    <span className="font-mono text-[11px] text-[#7e8ba6] flex items-center gap-1.5">
                      <MethodBadge method={response.method as HttpMethod} />
                      {response.resolvedPath}
                    </span>
                  </div>
                </div>
              )
            })()}

            {/* Extracted auto-fill values */}
            {extractedValues.length > 0 && (
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-[#3f4f6b] mb-1.5">
                  Extracted Values · Click to save for auto-fill
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {extractedValues.map(([key, val]) => (
                    <button
                      key={key}
                      onClick={() => handleAutoFillSave(key, val)}
                      className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[11px] transition-all"
                      style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}
                      title={`Click to save ${key} for auto-fill in path params`}
                    >
                      <span className="text-emerald-400 font-semibold">{key}</span>
                      <span className="text-[#94a3b8] font-mono truncate max-w-[200px]">{val}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Response body */}
            <div className="rounded-[14px] overflow-hidden" style={{ background: '#0e1828', border: '1px solid #1e2c44' }}>
              <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid #1e2c44', background: '#0a1322' }}>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Response Body</span>
                <CopyButton text={responseJson} />
              </div>
              <div className="p-3" style={{ background: '#0a1322', maxHeight: 480, overflowY: 'auto' }}>
                <pre
                  className="text-[11px] font-mono whitespace-pre-wrap break-all"
                  style={{ lineHeight: 1.6, color: '#94a3b8' }}
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  dangerouslySetInnerHTML={{ __html: syntaxHighlight(displayedLines) }}
                />
                {isTruncated && (
                  <button
                    onClick={() => setShowAllLines(v => !v)}
                    className="mt-2 text-[10px] text-[#6366f1] hover:underline"
                  >
                    {showAllLines ? 'Show less' : `Show all ${responseLines.length} lines`}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* History panel */}
        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #1e2c44' }}>
          <button
            onClick={() => setHistoryOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#111c30]"
            style={{ background: '#0e1828' }}
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Request History</span>
              {history.length > 0 && (
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                  style={{ background: '#0a1322', color: '#5b6b85' }}
                >
                  {history.length}
                </span>
              )}
            </div>
            {historyOpen
              ? <ChevronUp size={11} className="text-[#5b6b85]" />
              : <ChevronDown size={11} className="text-[#5b6b85]" />}
          </button>

          {historyOpen && (
            <div style={{ background: '#0a1322', borderTop: '1px solid #1e2c44' }}>
              {history.length === 0 ? (
                <p className="px-4 py-4 text-[11px] text-[#3f4f6b] italic">No history yet</p>
              ) : (
                <>
                  <div className="flex justify-end px-4 py-2" style={{ borderBottom: '1px solid #1a2740' }}>
                    <button
                      onClick={clearHistory}
                      className="text-[10px] text-[#5b6b85] hover:text-red-400 transition-colors"
                    >
                      Clear all
                    </button>
                  </div>
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    {history.map(entry => (
                      <button
                        key={entry.id}
                        onClick={() => replayHistory(entry)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-[#111c30]"
                        style={{ borderBottom: '1px solid #1a2740' }}
                      >
                        <MethodBadge method={entry.method} />
                        <span className="text-[10px] font-mono text-[#7e8ba6] truncate flex-1">
                          {entry.resolvedPath}
                        </span>
                        <StatusBadge code={entry.responseStatus} />
                        <span className="text-[9px] text-[#3f4f6b] shrink-0">
                          {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="text-[9px] text-[#3f4f6b] shrink-0">{entry.durationMs}ms</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
